import React, { useState } from 'react';
import { Button, Dropdown, Form, Typography, Upload, message, Radio, DatePicker, Spin, Menu } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import Select from 'components/common/select/Select';
import { useChannelsPaging } from 'hooks/channels/useChannelsPaging';
import { useWardsByDistrictId } from 'hooks/administrative-units/useWardsByDistrictId';
import { useOrganizationUnitsPagingWithoutUserOrganizationUnit } from 'hooks/organization-units';
import SelectServiceTypes from 'components/service-types/SelectServiceTypes';
import dayjs from 'dayjs';
import { useAppointmentDate } from 'hooks/tickets/useGetAppointmentDate';
import { useServiceTypeDetail } from 'hooks/service-types/useGetServiceTypeDetail';
import { createTicketMessages } from 'messages/create-ticket.messages';
import { useCreateTicketsFromExcelMutation } from 'services/create-ticket-from-excel/create-ticket-from-excel.api';
import { configuration } from 'utils/configuration';

/**
 * A simplified version of CreateTicketForm that is used on the Excel page.
 * It only shows the following fields:
 *   • Channel (Kênh liên hệ)
 *   • Ward (Phường)
 *   • Upload file area
 *   • Service type dropdown
 *   • Three action buttons: chuyển đơn vị, chuyển cấp trên, hoàn tất tại trung tâm
 *
 * All modal logic is removed. Clicking a button will simply validate the fields and emit a console message.
 */

export type CreateTicketFormExcelValues = {
    channelId?: number;
    ward?: number;
    uploadFileIds?: UploadFile[];
    serviceTypeId?: number;
    identificationType?: 'identified' | 'unidentified';
    appointmentDate?: dayjs.Dayjs;
};

const DISTRICT_ID_FIXED = 90000; // As per global requirement, district is fixed

interface CreateTicketFormExcelProps {
    onSuccess?: () => void;
    onCancel?: () => void;
    initialValues?: Partial<CreateTicketFormExcelValues>;
}

type IdentificationType = 'identified' | 'unidentified';

const CreateTicketFormExcelComponent = () => {
    const [form] = Form.useForm<CreateTicketFormExcelValues>();
    const { channels } = useChannelsPaging({});
    const { wards } = useWardsByDistrictId(DISTRICT_ID_FIXED);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [appointmentDate, setAppointmentDate] = useState(dayjs());
    const [selectedOrgUnitName, setSelectedOrgUnitName] = useState<string>('');
    const { organizationsUnits } = useOrganizationUnitsPagingWithoutUserOrganizationUnit({
        organizationUnitClassifyId: [7]
    });

    // Allowed MIME types for Excel files
    const EXCEL_MIME_TYPES = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/octet-stream',
        'application/xls',
        'application/x-xls',
    ];

    const handleBeforeUpload = (file: File) => {
        const isExcel = EXCEL_MIME_TYPES.includes(file.type) ||
            file.name.endsWith('.xls') ||
            file.name.endsWith('.xlsx');

        if (!isExcel) {
            message.error('Chỉ được phép tải lên file Excel (.xls, .xlsx)');
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const handleUploadChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        // Filter out any files that might have been added despite validation (e.g., drag and drop)
        const validFiles = newFileList.filter(file => {
            const fileName = file.name || '';
            const isExcel = EXCEL_MIME_TYPES.includes(file.type || '') ||
                fileName.endsWith('.xls') ||
                fileName.endsWith('.xlsx');
            return isExcel;
        });

        setFileList(validFiles);
    };

    // Fetch default appointment date from API
    const { data: appointmentDateData, isLoading: isAppointmentLoading } = useAppointmentDate('');

    // Watch selected service type detail
    const serviceTypeIdSelected = Form.useWatch('serviceTypeId', form);
    const { serviceType } = useServiceTypeDetail(serviceTypeIdSelected as number);

    // Update appointment date whenever API returns or service type changes
    React.useEffect(() => {
        let newDate = dayjs(appointmentDateData?.data);
        const urgentCodes = ['baoMatDien', 'anToanDien', 'baoMatDienDoNo'];
        if (serviceType && urgentCodes.includes(serviceType.code)) {
            newDate = dayjs();
        }
        setAppointmentDate(newDate);
        form.setFieldValue('appointmentDate', newDate);
    }, [serviceType?.code, appointmentDateData?.data]);

    const [createTicketsFromExcel, { isLoading: submitting }] = useCreateTicketsFromExcelMutation();

    const onSubmit = (type: 'orgUnit' | 'supervisor' | 'finish') => {
        // Log current form values before validation
        const currentValues = form.getFieldsValue();
        console.log('📋 Form values before submit:', currentValues);
        console.log('🔍 Ward value type:', typeof currentValues.ward);

        form
            .validateFields()
            .then((values) => {
                const statusIdMap = {
                    'orgUnit': '6',      // transferProcessingUnit
                    'supervisor': '9',   // redirectToTheCenter
                    'finish': '8'        // Hoàn tất tại trung tâm
                };

                const formData = new FormData();
                formData.append('channelId', String(values.channelId));
                formData.append('serviceTypeId', String(values.serviceTypeId));
                formData.append('appointmentDate', (values.appointmentDate as dayjs.Dayjs).toISOString());
                formData.append('statusId', statusIdMap[type]);
                formData.append('isCustomerUndefined', String(values.identificationType === 'unidentified'));
                formData.append('administrativeUnitId', String(values.ward));

                // add file (assuming first file)
                if (fileList[0]?.originFileObj) {
                    formData.append('file', fileList[0].originFileObj as File);
                }

                createTicketsFromExcel(formData)
                    .unwrap()
                    .then((response) => {
                        message.success(response?.message || 'Đã gửi phiếu thành công');
                        form.resetFields();
                        setFileList([]);
                    })
                    .catch((err) => {
                        console.error('API error', err);
                        message.error(err?.data?.message || 'Gửi phiếu thất bại');
                    });
                form.resetFields();
                setFileList([]);
                setSelectedOrgUnitName('');

            })
            .catch((err) => {
                console.error('❌ [CreateTicketFormExcel] Validation failed:', err);
                // Show detailed validation errors
                if (err && err.errorFields) {
                    console.log('🚨 Validation errors:', err.errorFields);
                }
            });
    };

    // 📄 Excel template links
    const templateLinks = {
        unidentified: `${configuration.MINIO_URL}system-files/template-create-tickets-omni-khongdinhdanh.xlsx`,
        identified: `${configuration.MINIO_URL}system-files/template-create-tickets-omni-dinhdanh.xlsx`
    } as const;

    const handleDownloadTemplate = (type: 'unidentified' | 'identified') => {
        const url = templateLinks[type];
        if (!url) {
            console.error('Template URL not found for type:', type);
            return;
        }

        console.log('Downloading template from:', url);

        // Tạo link tải xuống trực tiếp
        const link = document.createElement('a');
        link.href = url;
        link.download = type === 'unidentified'
            ? 'template-khong-dinh-danh.xlsx'
            : 'template-dinh-danh.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Handler for ward selection to find and display organization unit
    const handleWardChange = (value: number) => {

        // Explicitly set the form field value
        form.setFieldsValue({ ward: value });

        // Find the selected ward
        const selectedWard = wards?.find(ward => ward.administrativeUnitId === value);

        if (selectedWard?.organizationUnitCode) {
            // Find the organization unit using the ward's organizationUnitCode
            const orgUnit = organizationsUnits.find(
                org => org.code === selectedWard.organizationUnitCode
            );

            // Set the organization unit name to display
            setSelectedOrgUnitName(orgUnit?.name || '');

            // Log form values after selection
            setTimeout(() => {
                const currentValues = form.getFieldsValue();

            }, 100);
        } else {
            setSelectedOrgUnitName('');
        }
    };

    // Helper function to remove Vietnamese tones for search
    const removeVietnameseTones = (str: string): string => {
        return str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // remove diacritics
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
    };

    return (
        <div className='p-6'>
            <div className='mb-6 flex items-center justify-between'>
                <Typography.Title level={4} className='mb-0'>{'Tạo phiếu từ file excel'}</Typography.Title>
                <Dropdown
                    overlay={
                        <Menu>
                            <Menu.Item key="unidentified" onClick={() => handleDownloadTemplate('unidentified')}>
                                Tải file tạo phiếu không định danh
                            </Menu.Item>
                            <Menu.Item key="identified" onClick={() => handleDownloadTemplate('identified')}>
                                Tải file tạo phiếu định danh
                            </Menu.Item>
                        </Menu>
                    }
                    trigger={['click']}
                >
                    <Button type='primary' icon={<DownloadOutlined />}>
                        Tải file mẫu
                    </Button>
                </Dropdown>
            </div>
            <Form form={form} layout='vertical'>
                {/* Identification Type */}
                <Form.Item
                    label='Loại khách hàng'
                    name='identificationType'
                    initialValue='unidentified'
                    rules={[{ required: true, message: 'Vui lòng chọn loại khách hàng' }]}
                >
                    <Radio.Group>
                        <Radio value='unidentified'>Không định danh</Radio>
                        <Radio value='identified'>Định danh</Radio>
                    </Radio.Group>
                </Form.Item>

                {/* Channel */}
                <Form.Item
                    label='Kênh tiếp nhận'
                    name='channelId'
                    rules={[{ required: true, message: 'Vui lòng chọn kênh tiếp nhận' }]}
                >
                    <Select
                        placeholder={'Kênh liên hệ'}
                        options={channels?.map((ch) => ({ label: ch.channelName, value: ch.channelId })) || []}
                        showSearch
                    />
                </Form.Item>

                {/* Ward */}
                <Form.Item
                    label={'Phường'}
                    name='ward'
                    required
                    rules={[
                        {
                            required: true,
                            message: createTicketMessages.wardRequired
                        }
                    ]}
                >
                    <Select
                        placeholder={createTicketMessages.selectWard}
                        options={wards?.map((w) => ({ label: w.administrativeUnitName, value: w.administrativeUnitId })) || []}
                        showSearch
                        filterOption={(input, option) =>
                            removeVietnameseTones(String(option?.label || ''))
                                .toLowerCase()
                                .includes(removeVietnameseTones(String(input)).toLowerCase())
                        }
                        onChange={handleWardChange}
                    />
                    {selectedOrgUnitName && (
                        <div className="mt-2">
                            <Typography.Text className="text-[14px] text-[#141414] font-medium">
                                Đơn vị: {selectedOrgUnitName}
                            </Typography.Text>
                        </div>
                    )}
                </Form.Item>

                {/* Service Type */}
                <Form.Item
                    label={'Loại dịch vụ'}
                    name='serviceTypeId'
                    rules={[{ required: true, message: 'Vui lòng chọn loại dịch vụ' }]}
                >
                    <SelectServiceTypes isActive={true} classifyId={[62]} style={{ width: '100%' }} />
                </Form.Item>

                {/* Appointment Date */}
                <Form.Item
                    label='Ngày hẹn xử lý'
                    name='appointmentDate'
                    rules={[{ required: true, message: 'Vui lòng chọn ngày hẹn' }]}
                >
                    <DatePicker
                        format='DD/MM/YYYY'
                        style={{ width: '100%' }}
                        value={appointmentDate}
                        onChange={(date) => {
                            setAppointmentDate(date || dayjs());
                        }}
                        disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                </Form.Item>

                {/* Upload */}
                <Form.Item<CreateTicketFormExcelValues>
                    label={createTicketMessages.attachFile}
                    name='uploadFileIds'
                    valuePropName='fileList'
                    getValueFromEvent={(e) => e.fileList}
                    rules={[
                        {
                            required: true,
                            validator: (_, value) => {
                                if (!value || value.length === 0) {
                                    return Promise.reject(new Error('Vui lòng tải lên ít nhất một file Excel'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <Upload.Dragger
                        maxCount={1}
                        fileList={fileList}
                        beforeUpload={handleBeforeUpload}
                        onChange={handleUploadChange}
                        onRemove={() => {
                            setFileList([]);
                            return true;
                        }}
                        showUploadList={{ showRemoveIcon: true }}
                        accept='.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                        className={fileList.length > 0 ? 'upload-dragger-hide' : 'upload-dragger-required'}
                    >
                        <p className='ant-upload-drag-icon'>
                            <PlusOutlined />
                        </p>
                        <p className='ant-upload-text'>{createTicketMessages.attachFile}</p>
                    </Upload.Dragger>
                </Form.Item>

                {/* Action Buttons */}
                <Form.Item>
                    <div className='flex justify-center gap-4 mt-8'>
                        <Button
                            type='primary'
                            danger
                            size='large'
                            onClick={() => onSubmit('orgUnit')}
                            style={{ minWidth: '200px' }}
                        >
                            {createTicketMessages.transferProcessingUnit}
                        </Button>
                        <Button
                            type='primary'
                            size='large'
                            onClick={() => onSubmit('supervisor')}
                            style={{ minWidth: '200px' }}
                        >
                            <span className='font-semibold'>{createTicketMessages.redirectToTheCenter}</span>
                        </Button>
                        <Button
                            type='primary'
                            className='bg-green-700 hover:bg-green-800'
                            size='large'
                            onClick={() => onSubmit('finish')}
                            style={{ minWidth: '200px' }}
                        >
                            <span className='font-semibold'>Hoàn tất tại trung tâm</span>
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </div>
    );
};

export default CreateTicketFormExcelComponent;
